
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "./styles.css";
import MuseumWalkthrough from "./MuseumWalkthrough";
import ProofOfCultureLine from "./ProofOfCultureLine";

// Proof of Culture is live at /proof-of-culture but unlinked until the exhibition opens (Thu Oct 15).
// Flip to true on launch day to show it in the desktop + mobile menus and the homepage button.
const SHOW_POC_LINKS = true;

// Where the "Submit Artwork Proposal" button on /donate-art goes (the Typeform art donation form).
const ART_DONATION_FORM_URL = "https://form.typeform.com/to/IyStrwH6";


type WorkSeed = {
  source?: "Tezos" | "Ethereum";
  chain?: "ethereum" | "base" | "zora" | "polygon";
  contract?: string;
  tokenId?: string;
  artist: string;
  title: string;
  year: string;
  media?: string;
  mediaType?: "image" | "video";
  animation?: string;
  country?: string;
  cropPosition?: string;
};
type Work = WorkSeed & {
  media?: string;
  mediaType?: "image" | "video";
  animation?: string;
};
const IPFS_GATEWAYS = [
  "https://dweb.link/ipfs/",
  "https://ipfs.io/ipfs/",
  "https://gateway.pinata.cloud/ipfs/",
  "https://nftstorage.link/ipfs/",
];
function cleanIpfs(uri?: string) {
  if (!uri) return "";
  if (uri.startsWith("ipfs://ipfs/")) return IPFS_GATEWAYS[0] + uri.replace("ipfs://ipfs/", "");
  if (uri.startsWith("ipfs://")) return IPFS_GATEWAYS[0] + uri.replace("ipfs://", "");
  return uri;
}
function ipfsFallbacks(url?: string): string[] {
  if (!url) return [];
  const match = IPFS_GATEWAYS.find((g) => url.startsWith(g));
  if (!match) return [url];
  const cid = url.slice(match.length);
  return IPFS_GATEWAYS.map((g) => g + cid);
}
function guessMediaType(uri?: string): "image" | "video" {
  const lower = (uri || "").toLowerCase();
  if (lower.includes(".mp4") || lower.includes(".webm") || lower.includes(".mov")) return "video";
  return "image";
}
async function readJsonSafely(res: Response) {
  const text = await res.text();
  if (!text || text.trim().startsWith("<")) {
    console.warn("Expected JSON but received HTML/non-JSON response");
    return {};
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn("Expected JSON but received non-JSON response", error);
    return {};
  }
}
const featuredSeeds: WorkSeed[] = [
  { source: "Ethereum", chain: "ethereum", contract: "0x3b3ee1931dc30c1957379fac9aba94d1c48a5405", tokenId: "31396", artist: "Yatreda", title: "Queen of Sheba", year: "2021", media: "https://raw2.seadn.io/ethereum/0x3b3ee1931dc30c1957379fac9aba94d1c48a5405/26aec4a22cbd9a26a8c2ae653f61e1/6126aec4a22cbd9a26a8c2ae653f61e1.mp4", animation: "https://raw2.seadn.io/ethereum/0x3b3ee1931dc30c1957379fac9aba94d1c48a5405/26aec4a22cbd9a26a8c2ae653f61e1/6126aec4a22cbd9a26a8c2ae653f61e1.mp4", mediaType: "video", country: "Ethiopia" },
  { source: "Ethereum", chain: "ethereum", contract: "0x78d4bd3b4217b82561e53b791467ad87b4b0f8e5", tokenId: "51", artist: "IX Shells", title: "On Being Granted Entry (Elisabeth Jerichau-Baumann)", year: "2026", media: "/media/collection/ix-shells-on-being-granted-entry.mp4", mediaType: "video", country: "Panama" },
  { source: "Ethereum", chain: "ethereum", contract: "0x37e5001f26e70caab09f72d209c63e67d0d631e0", tokenId: "18", artist: "Linda Dounia Rebeiz", title: "Nqual 4", year: "2025", media: "/media/collection/linda-dounia-rebeiz-nqual-4.mp4", mediaType: "video", country: "Senegal" },
  { source: "Ethereum", chain: "polygon", contract: "0x3ab26b9bdae3f7ae4ad6fa88f07546659f7f201b", tokenId: "55", artist: "Diana Sinclair", title: "Confronting Mortality", year: "2021", media: "https://raw2.seadn.io/polygon/0x3ab26b9bdae3f7ae4ad6fa88f07546659f7f201b/aee1f5017820f549a916f988394e33/7aaee1f5017820f549a916f988394e33.mp4", animation: "https://raw2.seadn.io/polygon/0x3ab26b9bdae3f7ae4ad6fa88f07546659f7f201b/aee1f5017820f549a916f988394e33/7aaee1f5017820f549a916f988394e33.mp4", mediaType: "video", country: "United States" },
  { source: "Tezos", contract: "KT18bkP5fJ9ocuJZVeruKxHgufb4XV546HyX", tokenId: "0", artist: "David Okhn", title: "Bond", year: "2022", media: "/media/collection/david-okhn-bond.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "hicetnunc", tokenId: "372324", artist: "William Mapan", title: "Recursive Painting #03", year: "2021", media: "/media/collection/williamapan-recursive-painting-03.mp4", mediaType: "video", country: "France" },
  { source: "Tezos", contract: "hicetnunc", tokenId: "824961", artist: "Janice Mascarenhas", title: "My Beauty Saloon is Here", year: "2023", media: "/media/collection/janice-mascarenhas-my-beauty-saloon-is-here.jpg", country: "Brazil" },
  { source: "Ethereum", chain: "base", contract: "0xdf42589528ed5d3461fe521e9ac3557838919112", tokenId: "1", artist: "Cosmic Bagels", title: "One Morning In Mamelles", year: "2024", media: "/media/collection/cosmic-bagels-one-morning-in-mamelles.jpg", country: "Senegal" },

];
const recentSeeds: WorkSeed[] = [
  { source: "Tezos", contract: "KT1LS6oAbywyVu314uS9JuSciE26m6ykrUqm", tokenId: "15", artist: "Kamau Kamau", title: "Floral Firmware", year: "2026", media: "/media/collection/kamau-kamau-floral-firmware.mp4", mediaType: "video", country: "Kenya" },
  { source: "Ethereum", chain: "ethereum", contract: "0xa47b61aa4852d1a1489ffdd75d082040c15a89c7", tokenId: "1", artist: "Okafor Anita Chinemerem (Kafani)", title: "They Called It Medicine", year: "2026", media: "/media/collection/kafani-they-called-it-medicine.jpg", country: "Nigeria" },
  { source: "Ethereum", chain: "ethereum", contract: "0x1a9810f2f1cb08837775695969441b56ab2ddc97", tokenId: "1", artist: "0xdiablo", title: "Satyr", year: "2026", media: "/media/collection/0xdiablo-satyr.avif", country: "Nigeria" },
  { source: "Tezos", contract: "KT1D9k3A7Pyk7qq1sgEsKoqi7NCNXkNWaiqZ", tokenId: "2", artist: "Linda Dounia Rebeiz", title: "psalm 30", year: "2026", media: "/media/collection/linda-dounia-rebeiz-psalm-30.mp4", mediaType: "video", country: "Senegal" },
  { source: "Ethereum", chain: "ethereum", contract: "0x2bd8186e8e68066cec7d43c005c4063b0894c6e7", tokenId: "3", artist: "Khadeejat Ize and Tatak Waskitho", title: "Under The Rain", year: "2026", media: "/media/collection/khadeejat-tatak-under-the-rain.gif", country: "Nigeria & Indonesia" },
  { source: "Tezos", contract: "KT1QHHr499yH1aMhXjrJ2XtHViKMXiwGqtq7", tokenId: "3", artist: "Osinachi", title: "The Apprentice", year: "2026", media: "/media/collection/osinachi-the-apprentice.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "open_objkt", tokenId: "42827", artist: "Umi-Amara Ibrahim", title: "Change Your Mind", year: "2026", media: "/media/collection/umi-amara-ibrahim-change-your-mind.jpg", country: "Nigeria", cropPosition: "center 60%" },
  { source: "Tezos", contract: "KT1W3kbN3VGBeoV3sMtykC1E7fQta4gfkivD", tokenId: "0", artist: "Tatchero collective", title: "Against All Odds", year: "2022", media: "/media/collection/tatchero-against-all-odds.jpg", country: "Various" },

];
const collageSeeds: WorkSeed[] = [
  { source: "Ethereum", chain: "zora", contract: "0x928308745e83571b29483eb6d064bf7f285f9035", tokenId: "3", artist: "OurDailyBread", title: "Who I am", year: "2024", media: "/media/collection/ourdailybread-who-i-am.avif" },
  { source: "Ethereum", chain: "zora", contract: "0xa83a78e5e1f4220efa37d37c70a0995034dcab5c", tokenId: "5", artist: "Vd0Artist", title: "A Chair is Just a Chair", year: "2024", media: "/media/collection/vd0artist-a-chair-is-just-a-chair.avif", country: "United States" },
  { source: "Tezos", contract: "KT1R5y6B33vR3V4etFPXPhEamuvbjwrhF3jM", tokenId: "24", artist: "Thais Silva", title: "Abstraction", year: "2023", media: "/media/collection/black-collage-abstraction.jpg", country: "Brazil" },
  { source: "Tezos", contract: "KT1C5P5EKb35JTX9gEgdk3h8NQ5jZJGWDwij", tokenId: "2", artist: "Daniel Martinez", title: "Hallazgo III", year: "2022", media: "/media/collection/daniel-martinez-hallazgo-iii.jpg", country: "Cuba" },
  { source: "Tezos", contract: "KT1AdJy6boHYYpBU62sizJfrWNZ6BKn7dQKw", tokenId: "9", artist: "Adebowale A.", title: "TRUE", year: "2022", media: "/media/collection/adebowale-a-true.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1RqL4RZ6Mnoxx2xf2pLtYF9TCq28Go5fzE", tokenId: "0", artist: "Shawntel Johnson", title: "Polena", year: "2022", media: "/media/collection/shawntel-johnson-polena.jpg", country: "United States" },
  { source: "Tezos", contract: "hicetnunc", tokenId: "60158", artist: "Thais Silva", title: "Crianças são futuro", year: "2021", media: "/media/collection/thais-silva-criancas-sao-futuro.jpg", country: "Brazil" },

];
const sectionDescriptions: Record<string, string> = {
  "Featured Works":
    "A rotating selection of anchor works within the collection, chosen for their formal rigor and cultural significance. These works establish the terms through which the broader archive is encountered.",
  "Recent Additions":
    "New acquisitions that reflect the archive in progress. They register shifts in practice and attention as well as what is being made and what is being chosen to remain.",
  Portraiture:
    "Works that center the body as a site of identity, presence, and construction. Representation here is shaped, asserted, and at times deliberately unsettled.",
  Figuration:
    "Works that engage the body as form, gesture, and visual structure. The figure becomes a way to think through movement, symbolism, and the constructed image.",
  Photography:
    "Lens-based works that operate between document and interpretation. These images engage with presence and perception, often complicating the act of looking itself.",
  "Time-Based":
    "Works structured through duration and movement. Time functions as a compositional element, shaping both the form of the work and the conditions of its reception.",
  Generative:
    "Works shaped by systems, rules, and computational processes, from artist-written code to models trained on existing images and language. Through variation, interpretation, and the artist\u2019s choices, these processes become part of how meaning is made.",
  "Collage & Mixed Media":
    "Works built through accumulation and recombination. Fragments are not incidental, they are the material through which new relationships, references, and visual structures are formed.",
};
const timeBasedSeeds: WorkSeed[] = [
  { source: "Ethereum", chain: "ethereum", contract: "0xe1bd4b40079cd73d5d55426274a94ee662c0c67b", tokenId: "10900010519", artist: "Micah Johnson", title: "Genesis.001 #519", year: "2022", media: "/media/collection/micah-johnson-genesis-001.mp4", mediaType: "video", country: "United States" },
  { source: "Ethereum", chain: "zora", contract: "0x8d83a0a5cab5b2706a9f26da493c1a6c2815bf31", tokenId: "208", artist: "Latasha", title: "Lala Land", year: "2020", media: "/media/collection/latasha-lala-land.mp4", mediaType: "video", country: "United States" },
  { source: "Ethereum", chain: "zora", contract: "0x21eedbba84befdd4db256ebf6b3db5fc76b49d72", tokenId: "1", artist: "Heno", title: "Hypersurveilled Collection Trailer", year: "2024", media: "/media/collection/heno-hypersurveilled-collection-trailer.mp4", mediaType: "video", country: "United States" },
  { source: "Ethereum", chain: "base", contract: "0x9cd7e5fc517f9527150ad5ce50d5698daafeed5b", tokenId: "14", artist: "Nygilia", title: "Mode 131313", year: "2024", media: "/media/collection/nygilia-mode-131313.mp4", mediaType: "video", country: "United States" },
  { source: "Ethereum", chain: "base", contract: "0x24687ded4c997be4aaf3dc7643b7af091b621072", tokenId: "82", artist: "Ed Balloon", title: "The Narrator", year: "2024", media: "/media/collection/ed-balloon-the-narrator.mp4", mediaType: "video", country: "United States" },
  { source: "Ethereum", chain: "ethereum", contract: "0x043afa54ce8dd31915df4e7678d213fcab65051b", tokenId: "2", artist: "Hyper9", title: "Moonlight", year: "2021", media: "/media/collection/hyper9-moonlight.mp4", mediaType: "video", country: "United States" },
  { source: "Tezos", contract: "KT1WtWkqaGcc18XVEQzBmUTbYFj9oPYn5CRe", tokenId: "2", artist: "janice mascarenhas", title: "girls", year: "2025", media: "/media/collection/janice-mascarenhas-girls.mp4", mediaType: "video", country: "Brazil" },

];
const portraitureSeeds: WorkSeed[] = [
  { source: "Tezos", contract: "KT1U7ayGZcn6i6nja2MSMa1PQYpqoCHdDpVF", tokenId: "6", artist: "Mercy Thokozane Minah", title: "lovers for life", year: "2023", media: "/media/collection/iamafreedom-lovers-for-life.jpg", country: "South Africa" },
  { source: "Tezos", contract: "KT1EGGefWemGh6FMQccodQwJLVyR8eDjgjwV", tokenId: "2", artist: "Ada Amadi", title: "Everything That Is You", year: "2022", media: "/media/collection/ada-amadi-everything-that-is-you.jpg", country: "Kenya", cropPosition: "center 78%" },
  { source: "Ethereum", chain: "zora", contract: "0x1d4f46d26409e41d0c0b81762a818015ed1ac030", tokenId: "3", artist: "Lana Denina", title: "I'm a Clown for Ever Loving You", year: "2024", media: "/media/collection/lana-denina-im-a-clown.avif" },
  { source: "Ethereum", chain: "ethereum", contract: "0x495f947276749ce646f68ac8c248420045cb7b5e", tokenId: "63671613449232605144879725342296715579569828211391776644594841428281780076545", artist: "Victoria Oniosun", title: "Fragilidad (Fragility)", year: "2022", media: "/media/collection/adiree-fragilidad.avif", country: "Nigeria" },
  { source: "Ethereum", chain: "ethereum", contract: "0x495f947276749ce646f68ac8c248420045cb7b5e", tokenId: "110340131402893691066174558107776754684929842295194860553026492939644333719553", artist: "Jeanette Madden", title: "Moving On", year: "2021", media: "/media/collection/jeannette-wilkins-moving-on.avif", country: "United States", cropPosition: "center 0%" },
  { source: "Ethereum", chain: "base", contract: "0x1104a6913501a77a3885d8608cef84afcdb70301", tokenId: "3", artist: "Jay Golding Art", title: "A Gaze At Waves", year: "2023", media: "/media/collection/wata-a-gaze-at-waves.avif", country: "Jamaica" },
  { source: "Tezos", contract: "open_objkt", tokenId: "4039", artist: "Ahmed Partey", title: "Mastodon", year: "2025", media: "/media/collection/ahmed-partey-mastodon.jpg", country: "Ghana" },
  { source: "Tezos", contract: "KT1XEKf1B7wbYU7i6HtTdYnfYv7GRq6TJ5tm", tokenId: "1", artist: "Chigo Artish", title: "Topaz", year: "2022", media: "/media/collection/chigo-artish-topaz.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1TrjiAXWpQWhBzTQfNG72puMNajUsUCK7w", tokenId: "1", artist: "Nygilia", title: "Prisma 2", year: "2022", media: "/media/collection/nygilia-prisma-2.jpg", country: "United States" },
  { source: "Tezos", contract: "KT1WcqsCQWdjwGpBFJxyL3XXaNse9hxCWM77", tokenId: "6", artist: "Paul Adeoye", title: "Smile", year: "2022", media: "/media/collection/paul-adeoye-smile.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1M1hXAmG922qJb3L2ZE2xrXZB8jHzDmREC", tokenId: "9", artist: "Auriea Harvey", title: "2021-07-20 00_57_04- fauna-New-AR.png", year: "2025", media: "/media/collection/auriea-harvey-fauna.jpg" },
  { source: "Tezos", contract: "KT1AdJy6boHYYpBU62sizJfrWNZ6BKn7dQKw", tokenId: "6", artist: "Adebowale A.", title: "Pray", year: "2022", media: "/media/collection/adebowale-a-pray.jpg", country: "Nigeria" },

];
const figurationSeeds: WorkSeed[] = [
  { source: "Ethereum", chain: "ethereum", contract: "0x2f8bfaeea44536c0e43cd4f09ad56f4835ef2d3f", tokenId: "3", artist: "Chidiebere Anidi", title: "Trust The Process", year: "2022", media: "/media/collection/chidiebere-anidi-trust-the-process.avif", country: "Nigeria" },
  { source: "Tezos", contract: "KT193Pkq4Zz1UCgZZupeQjtutwEpxp67Trde", tokenId: "13", artist: "MOTE.MT", title: "With a lotto flavor from the conurbano", year: "2025", media: "/media/collection/mote-mt-with-a-lotto-flavor.jpg", country: "Argentina" },
  { source: "Tezos", contract: "open_objkt", tokenId: "7020", artist: "Hopiummm", title: "Red Sweaters & Ghosts", year: "2025", media: "/media/collection/hopiummm-red-sweaters-and-ghosts.webp", country: "Antigua & Barbuda" },
  { source: "Tezos", contract: "KT1TKhVwzRWsZpa4yYfH25M7o8rRqvcFUdjX", tokenId: "15", artist: "Fame Identity", title: "New Chassis", year: "2024", media: "/media/collection/fame-identity-new-chassis.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "hicetnunc", tokenId: "544869", artist: "Artedeft", title: "Esperar", year: "2021", media: "/media/collection/artedeft-esperar.jpg", country: "Brazil" },

];
const generativeSeeds: WorkSeed[] = [
  { source: "Tezos", contract: "hicetnunc", tokenId: "781128", artist: "Fingacode", title: "ay-Or-te", year: "2022", media: "/media/collection/fingacode-ay-or-te.mp4", mediaType: "video", country: "United Kingdom" },
  { source: "Ethereum", chain: "ethereum", contract: "0x000000412217f67742376769695498074f007b97", tokenId: "241", artist: "Fingacode", title: "Jazz #241", year: "2024", media: "/media/collection/fingacode-jazz-241.jpg", country: "United Kingdom" },
  { source: "Ethereum", chain: "zora", contract: "0x9ed089831d1252febe9d43da0b2cd5e4fa1faf1a", tokenId: "2", artist: "Aaron Ricketts", title: "Brain Juice", year: "2023", media: "/media/collection/aaron-ricketts-brain-juice.avif", country: "United States" },
  { source: "Tezos", contract: "KT1Vcae1TsLGnjZkmL873NHzbPLGMHeFL3e8", tokenId: "9", artist: "Kamau Kamau", title: "Burning the Midnight Oil", year: "2026", media: "/media/collection/kamau-kamau-burning-the-midnight-oil.jpg", country: "Kenya" },
  { source: "Ethereum", chain: "base", contract: "0x665abbc28c98ef1575d38f364ab3b44f3d00a247", tokenId: "93", artist: "Kamau Kamau", title: "Chromadelia", year: "2024", media: "/media/collection/kamau-kamau-chromadelia.jpg", country: "Kenya" },
  { source: "Ethereum", chain: "base", contract: "0xb2bb950ae1d4da449a378baac840449784e7189f", tokenId: "34", artist: "Vd0Artist", title: "Family Portrait #37", year: "2024", media: "/media/collection/vd0artist-family-portrait-37.jpg", country: "United States" },

];
const photographySeeds: WorkSeed[] = [
  { source: "Tezos", contract: "KT1CuUgzseTqah6qLcTvC6JEu2UGbG3DpN7C", tokenId: "0", artist: "Godiva Omoruyi", title: "Aurora", year: "2022", media: "/media/collection/godiva-omoruyi-aurora.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1N8txfub2ytr5eFCRWvn7vUzFWpxub1dKr", tokenId: "1", artist: "Charles Awelawa", title: "Celestial", year: "2022", media: "/media/collection/charles-awelawa-celestial.jpg", country: "Canada" },
  { source: "Tezos", contract: "KT1NnFxFBygEfpi4T6TEMmxY2f3EMmqEik8T", tokenId: "6", artist: "Tobiloba Ogunjobi", title: "Finding Hope", year: "2022", media: "/media/collection/tobiloba-finding-hope.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1Vu3V2HEocBuMyviZpCHyuAh9VoenkqkNY", tokenId: "5", artist: "Oyeniyi banji Andrew", title: "The comare", year: "2022", media: "/media/collection/oyeniyi-banji-andrew-the-comare.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1DZ5mVUGFATGwzfDFPNxD5xJf5LHfKma9d", tokenId: "8", artist: "Yahdiogo Ozi", title: "How you make me feel", year: "2022", media: "/media/collection/yahdiogo-ozi-how-you-make-me-feel.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1DPCqrz5GK6tD6uhrMgr7XcVCCYD7evZQd", tokenId: "5", artist: "Mo Rufy", title: "Passengers", year: "2022", media: "/media/collection/mo-rufy-passengers.jpg", country: "Nigeria" },
  { source: "Ethereum", chain: "zora", contract: "0xe3c3d93d6beb4eb56346d1263bd6f7b7340a0084", tokenId: "1", artist: "Ebuka Mordi", title: "Bridges across bytes", year: "2024", media: "/media/collection/ebuka-bridges-across-bytes.avif", country: "Nigeria" },
  { source: "Tezos", contract: "KT1Fa5VTv9xDEyJ8GmZHYwiDS8euDNRLuFJN", tokenId: "41", artist: "Segun Adewale", title: "Scarlet", year: "2024", media: "/media/collection/segun-adewale-scarlet.jpg", country: "Nigeria" },
  { source: "Tezos", contract: "KT1UXArz7SzecxhyqW2H9gkP3uHFZEGpp6bR", tokenId: "3", artist: "Real Alexxa Walker", title: "The Return", year: "2022", media: "/media/collection/real-alexxa-walker-the-return.jpg", country: "United States" },
  { source: "Tezos", contract: "KT1G7nXAoxoxAKjyJu9grc97edku9v72uZ3b", tokenId: "2", artist: "Daniel Martinez", title: "Oshun", year: "2022", media: "/media/collection/daniel-martinez-oshun.jpg", country: "Cuba" },
  { source: "Tezos", contract: "KT1QY1r98z4w3f4gG7AJuVbRSUNvXFoKFKu7", tokenId: "0", artist: "Femi Omoniwa", title: "AGBARA", year: "2026", media: "/media/collection/femi-omoniwa-agbara.jpg", country: "Nigeria" },

];
/* ---------------------------------------------------------
   TEAM PAGE DATA
   Photos live in /public/team/ — drop the matching files in
   from the ones provided alongside this code.
--------------------------------------------------------- */
type TeamMember = {
  name: string;
  role?: string;
  bio: string;
  photo: string;
  imagePosition?: string;
};
const founder: TeamMember = {
  name: "Sian Morson",
  role: "Founder, Executive Director & Chief Curator",
  bio: "Sian Morson has spent over a decade doing, quietly and consistently, the work MoDDA now formalizes. She holds degrees from NYU (Film & Television) and Middlesex University (MA Electronic Art), and was the first Entrepreneur in Residence at Cross Culture Ventures. Her curatorial work includes SuperRare and Art Basel programming: Zoratopia, La Maison Noir, and Nolcha Shows. She is a recipient of the L'Oreal Women in Digital Award and was named to the NEAR Foundation's Women in Web3 Changemakers list in 2023. Her work has been covered by the Wall Street Journal, Essence, Ebony, Forbes, Inc., and Bloomberg. Morson built MoDDA's collection through direct artist relationships since 2021. Proof of Culture, her essay series and podcast, is the intellectual foundation for MoDDA's institutional argument and the basis of her forthcoming book of the same name.",
  photo: "/team/sian-morson.png",
};
const foundingBoard: TeamMember[] = [
  {
    name: "Racquel Chevremont",
    bio: "Racquel Chevremont is an internationally recognized model, art curator, advisor, collector, and creative director with more than 25 years at the intersection of fashion, art, and culture. She has curated exhibitions for leading institutions and brought acclaimed art collections to film and television including Severance and And Just Like That.",
    photo: "/team/racquel-chevremont.jpg",
  },
  {
    name: "Rob Fields",
    bio: "Rob Fields is an art advisor specializing in investment-grade art from the African diaspora and Global South. Founder of Mighty Mighty LLC, he has facilitated acquisitions by Malick Sidibé, Mickalene Thomas, and Joana Choumali. He previously led Weeksville Heritage Center and directed the Sugar Hill Children's Museum.",
    photo: "/team/rob-fields.jpg",
  },
  {
    name: "Sabrina Hersi Issa",
    bio: "Sabrina Hersi Issa is a human rights technologist and civic infrastructure builder working at the intersections of technology, democracy, and power. She is founding Managing Partner of Democracy Well, a national movement network and collective fund, and a project of Bold Impact. She was previously an Emmy Award-winning producer.",
    photo: "/team/sabrina-hersi-issa.jpg",
  },
  {
    name: "Toni Marinara",
    bio: "Toni Marinara is an attorney and artist representative specializing in generative art. He is co-founder of ARTXCODE, a talent agency offering artist representation, private sales, and institutional advisory, helping generative artists navigate legal structures, market access, and long-term career strategy.",
    photo: "/team/toni-marinara.jpeg",
  },
  {
    name: "Lauren Ruffin",
    bio: "Lauren Ruffin bridges technology, art, and social impact as Director and Lead Strategist of Arts & Cultural Programming at Michigan Central's Detroit innovation campus. She previously served as Fractured Atlas Co-CEO, founding the Artist Campaign School, and co-founded CRUX Cooperative. A 2025 Lewis Latimer Fellow, she holds a J.D. from Howard.",
    photo: "/team/lauren-ruffin.png",
  },
];
// Shown on the Founding Board only on mobile, not on desktop
const sianBoardEntryMobile: TeamMember = {
  name: "Sian Morson",
  bio: "Sian Morson is the Founder, Executive Director, and Chief Curator of MoDDA. She has built the museum's collection through direct artist relationships since 2021, and hosts Proof of Culture, an essay series and podcast on diasporan digital art and culture.",
  photo: "/team/sian-morson.png",
};
// Mobile-only board order: foundingBoard is alphabetical by last name
// (Chevremont, Fields, Hersi Issa, Marinara, Ruffin) — Sian (Morson) slots
// in between Marinara and Ruffin rather than appearing first, so she
// doesn't read as "founder, then also first board member".
const foundingBoardMobile: TeamMember[] = [
  foundingBoard[0],
  foundingBoard[1],
  foundingBoard[2],
  foundingBoard[3],
  sianBoardEntryMobile,
  foundingBoard[4],
];
const advisoryCouncil: TeamMember[] = [
  {
    name: "Stefen Deleveaux",
    bio: "Stefen Deleveaux is a Bahamian economist, founder of The Oasis Group, and a leading authority on blockchain and digital currencies across the Caribbean and Global South.",
    photo: "/team/stefan-deleveaux.jpeg",
  },
  {
    name: "Amanda Gadbow",
    bio: "Amanda Gadbow is Director of Client Relations at Fountain Digital, working with top Web3 artists and collectors across five years in the space.",
    photo: "/team/amanda-gadbow.jpg",
  },
  {
    name: "Harold Hughes",
    bio: "Harold Hughes is Founder and CEO of Divi and NFNTE Capital, connecting entrepreneurs to the resources and networks they need to grow.",
    photo: "/team/harold-hughes.jpg",
  },
  {
    name: "Matt Mason",
    bio: "Matt Mason is CMO at KUNGFU.AI and a two-decade veteran of culture-shaping tech marketing, from BitTorrent to a Sony Pictures innovation lab.",
    photo: "/team/matt-mason.png",
  },
  {
    name: "Lady Pheønix",
    bio: "Lady Pheønix is a pioneering voice in digital art, credited with curating Proof Of Sovereignty, the first NFT auction at a major auction house.",
    photo: "/team/lady-pheonix.png",
    imagePosition: "0% center",
  },
  {
    name: "Sierra Renee",
    bio: "Sierra Renee, also known as JuuJuuMama, is a multidisciplinary artist and founder of ÍTÀN: West African Artistry, curating across the diaspora.",
    photo: "/team/sierra-renee.jpeg",
  },
  {
    name: "Kai Turner",
    bio: "Kai Turner is a GenAI creative director with 25+ years in product design and digital media, formerly at Sony Pictures and Netflix.",
    photo: "/team/kai-turner.png",
  },
];
async function resolveTezos(seed: WorkSeed): Promise<Work> {
  const params = new URLSearchParams({
    contract: seed.contract || "",
    tokenId: seed.tokenId || "",
  });
  const res = await fetch(`/api/tezos?${params}`);
  const data = await readJsonSafely(res);
  const token = data?.data?.token?.[0];
  const display = cleanIpfs(token?.display_uri || token?.thumbnail_uri);
  const artifact = cleanIpfs(token?.artifact_uri);
  const isVideo = token?.mime?.startsWith("video") || guessMediaType(artifact) === "video";
  const media = display || artifact;
  const mediaType = display ? "image" : isVideo ? "video" : guessMediaType(media);
  return { ...seed, media, mediaType, animation: isVideo ? artifact : undefined };
}
async function resolveEthereum(seed: WorkSeed): Promise<Work> {
  const chain = seed.chain || "ethereum";
  const params = new URLSearchParams({
    chain,
    contract: seed.contract || "",
    tokenId: seed.tokenId || "",
  });
  const res = await fetch(`/api/opensea?${params}`);
  const data = await readJsonSafely(res);
  const nft = data?.nft || {};
  let image = cleanIpfs(
    nft.display_image_url ||
      nft.image_url ||
      nft.raw_metadata?.image ||
      nft.metadata?.image ||
      nft.collection?.image_url
  );
  let animation = cleanIpfs(
    nft.display_animation_url ||
      nft.animation_url ||
      nft.raw_metadata?.animation_url ||
      nft.metadata?.animation_url
  );
  const metadataUrl = cleanIpfs(nft.metadata_url || nft.token_metadata || nft.metadata?.metadata_url || "");
  if ((!animation || !image) && metadataUrl) {
    // Same missing-timeout gap as the server-side proxies had: a plain fetch() with nothing to
    // stop it can hang indefinitely if this URL never responds, leaving the piece stuck on
    // "Loading" with no error ever surfacing.
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    try {
      const metaRes = await fetch(metadataUrl, { signal: controller.signal });
      const meta = await readJsonSafely(metaRes);
      image = image || cleanIpfs(meta?.image || meta?.image_url || meta?.display_image_url);
      animation = animation || cleanIpfs(meta?.animation_url || meta?.animation || meta?.media_url);
    } catch (error) {
      console.warn("Could not fetch token metadata", seed, error);
    } finally {
      window.clearTimeout(timer);
    }
  }
  const media = image || animation;
  const mediaType = image ? "image" : guessMediaType(animation);
  return { ...seed, media, mediaType, animation };
}
async function resolveWork(seed: WorkSeed): Promise<Work> {
  if (seed.media || seed.animation) return seed;
  try {
    if (seed.source === "Tezos") return await resolveTezos(seed);
    if (seed.source === "Ethereum") return await resolveEthereum(seed);
    return seed;
  } catch (error) {
    console.error("Could not resolve work", seed, error);
    return seed;
  }
}
function PlaceholderBox({ size }: { size: string }) {
  return (
    <div
      style={{
        height: size,
        borderRadius: "6px",
        backgroundColor: "#f1f1f1",
        border: "1px solid #e5e5e5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#999",
        fontSize: "13px",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      Image Placeholder
    </div>
  );
}
function ArtworkImage({ work, isFeatured = false }: { work: Work; isFeatured?: boolean }) {
  const [videoGatewayIdx, setVideoGatewayIdx] = useState(0);
  const [imageGatewayIdx, setImageGatewayIdx] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [gatewayIndex, setGatewayIndex] = useState(0);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const size = isFeatured ? "clamp(320px, 68vw, 520px)" : "clamp(300px, 68vw, 485px)";

  // Only fetch/mount video sources once the card is actually near the
  // viewport. Category rows render every work up front (not paginated),
  // so without this every video on a page would start downloading on
  // load regardless of whether it's ever scrolled into view.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || isNearViewport) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const commonStyle = {
    width: "100%",
    height: size,
    objectFit: "cover" as const,
    objectPosition: work.cropPosition || "center 20%",
    display: "block",
    borderRadius: "6px",
    backgroundColor: "#f1f1f1",
  };
  const playableVideo =
    work.animation ||
    (work.mediaType === "video" ? work.media : "") ||
    "";
  if (!videoFailed && playableVideo) {
    return (
      <div ref={containerRef} style={{ width: "100%", height: size }}>
        {isNearViewport ? (
          <video
            key={playableVideo}
            src={playableVideo}
            style={commonStyle}
            autoPlay
            muted
            loop
            playsInline
            controls={false}
            preload="metadata"
            onCanPlay={() => setVideoFailed(false)}
            onError={(event) => {
              console.warn("Video failed to load", playableVideo, event);
              setVideoFailed(true);
            }}
          />
        ) : (
          <PlaceholderBox size={size} />
        )}
      </div>
    );
  }
  const imageCandidates = ipfsFallbacks(work.media);
  const currentSrc = imageCandidates[gatewayIndex] || work.media;
  if (!imageFailed && currentSrc && work.mediaType !== "video") {
    return (
      <div ref={containerRef} style={{ width: "100%", height: size }}>
        <img
          key={work.media}
          src={currentSrc}
          alt={`${work.title} by ${work.artist}`}
          style={commonStyle}
          loading="lazy"
          onError={() => {
            if (gatewayIndex < imageCandidates.length - 1) {
              setGatewayIndex((i) => i + 1);
            } else {
              setImageFailed(true);
            }
          }}
        />
      </div>
    );
  }
  return (
    <div ref={containerRef} style={{ width: "100%", height: size }}>
      <PlaceholderBox size={size} />
    </div>
  );
}
function Footer() {
  return (
    <footer
  style={{
    marginTop: "0",
    padding: "32px clamp(24px, 4vw, 44px) 44px",
    borderTop: "1px solid #eee",
  }}
>
      <div style={{ maxWidth: "1200px" }}>
        {/* MoDDA label */}
        <div
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontSize: "16px",
            marginBottom: "8px",
          }}
        >
          MoDDA
        </div>
        {/* Tagline */}
        <div
          style={{
            fontSize: "9px",
            color: "#666",
            lineHeight: 1.6,
            maxWidth: "420px",
            marginBottom: "8px",
          }}
        >
          A home for Black digital art. A record for the future.
        </div>
        {/* Icons */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
          {/* Instagram */}
          <a href="https://www.instagram.com/Moddadotorg" target="_blank" rel="noreferrer">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17" cy="7" r="1" />
            </svg>
          </a>
          {/* Twitter/X */}
          <a href="https://x.com/moddadotorg" target="_blank" rel="noreferrer">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.5">
              <path d="M4 4L20 20M20 4L4 20" />
            </svg>
          </a>
        </div>
        {/* Copyright */}
        <div style={{ fontSize: "9px", color: "#aaa" }}>
          © 2026 MoDDA
        </div>
      </div>
    </footer>
  );
}
/* ---------------------------------------------------------
   TEAM PAGE COMPONENTS
--------------------------------------------------------- */
function TeamCard({ member, isMobile, compact = false }: { member: TeamMember; isMobile: boolean; compact?: boolean }) {
  return (
    <div>
      <img
        src={member.photo}
        alt={member.name}
        loading="lazy"
        style={{
          width: isMobile ? "140px" : "100%",
          aspectRatio: "4 / 5",
          objectFit: "cover",
          objectPosition: member.imagePosition || "center 20%",
          display: "block",
          borderRadius: "6px",
          backgroundColor: "#f1f1f1",
        }}
      />
      <div style={{ marginTop: compact ? "14px" : "20px", lineHeight: 1.65 }}>
        <div style={{ fontSize: compact ? "13px" : "15px", fontWeight: 700, letterSpacing: "0.01em" }}>{member.name}</div>
        {member.role && (
          <div style={{ color: "#666", fontSize: "12px", marginTop: "4px", letterSpacing: "0.02em" }}>
            {member.role}
          </div>
        )}
        <p style={{ color: "#555", fontSize: compact ? "11px" : "13px", marginTop: compact ? "8px" : "12px", lineHeight: 1.65 }}>{member.bio}</p>
      </div>
    </div>
  );
}
function TeamSection({ title, members, isMobile, columns }: { title: string; members: TeamMember[]; isMobile: boolean; columns?: number }) {
  const compact = (!!columns && columns >= 6) || isMobile;
  return (
    <section style={{ marginBottom: isMobile ? "56px" : "96px" }}>
      <h2
        style={{
          padding: isMobile ? "0 24px" : "0 44px",
          marginBottom: isMobile ? "30px" : "38px",
          fontFamily: "Cormorant Garamond, serif",
          fontSize: isMobile ? "26px" : "30px",
          fontWeight: 400,
          letterSpacing: "0.01em",
        }}
      >
        {title}
      </h2>
      <div
        style={{
          padding: isMobile ? "0 24px" : "0 44px",
          display: "grid",
          gridTemplateColumns: isMobile
            ? "repeat(2, 1fr)"
            : columns
            ? `repeat(${columns}, 1fr)`
            : "repeat(auto-fill, minmax(150px, 190px))",
          justifyContent: isMobile ? "stretch" : "start",
          gap: isMobile ? "32px 16px" : compact ? "24px 18px" : "32px 28px",
        }}
      >
        {members.map((member) => (
          <TeamCard key={member.name} member={member} isMobile={isMobile} compact={compact} />
        ))}
      </div>
    </section>
  );
}
const NEWSLETTER_STORAGE_KEY = "modda_newsletter_seen";
function NewsletterPopup({ isMobile }: { isMobile: boolean }) {
  const [visible, setVisible] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot, kept empty by real visitors
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const dismissForGood = () => {
    try {
      window.localStorage.setItem(NEWSLETTER_STORAGE_KEY, "1");
    } catch (_) {}
    setVisible(false);
  };

  useEffect(() => {
    let alreadySeen = false;
    try {
      alreadySeen = window.localStorage.getItem(NEWSLETTER_STORAGE_KEY) === "1";
    } catch (_) {}
    if (alreadySeen) return;

    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, 2500);

    const trigger = () => {
      if (!armed) return;
      setVisible(true);
      cleanup();
    };

    // Desktop: classic exit-intent -- cursor leaves through the top of the
    // viewport, heading for the tab bar / address bar / another window.
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) trigger();
    };
    // Mobile has no cursor to read exit-intent from, so approximate it:
    // once someone has scrolled down and then swipes back up near the top,
    // treat that as a signal they may be about to leave.
    let scrolledDown = false;
    const handleScroll = () => {
      const y = window.scrollY;
      if (y > 400) scrolledDown = true;
      else if (scrolledDown && y < 120) trigger();
    };

    if (isMobile) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      document.addEventListener("mouseleave", handleMouseLeave);
    }

    function cleanup() {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
    }
    return cleanup;
  }, [isMobile]);

  if (!visible) return null;

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, company, page: window.location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Something went wrong");
      }
      setStatus("done");
      try {
        window.localStorage.setItem(NEWSLETTER_STORAGE_KEY, "1");
      } catch (_) {}
    } catch (err: any) {
      setStatus("error");
      setErrorMessage(err && err.message ? err.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <div
      onClick={dismissForGood}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 17, 17, 0.55)",
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "440px",
          background: "#fff",
          borderRadius: "10px",
          padding: isMobile ? "36px 26px" : "48px 44px",
          textAlign: "center",
        }}
      >
        <button
          onClick={dismissForGood}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "16px",
            right: "18px",
            background: "none",
            border: "none",
            fontSize: "20px",
            lineHeight: 1,
            color: "#999",
            cursor: "pointer",
            padding: "4px",
          }}
        >
          &times;
        </button>
        {status === "done" ? (
          <>
            <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "28px", fontWeight: 500, margin: 0 }}>
              <em>Thank you.</em>
            </h2>
            <p style={{ color: "#555", fontSize: "15px", marginTop: "14px", lineHeight: 1.6 }}>
              You're on the list -- we'll be in touch.
            </p>
          </>
        ) : (
          <>
            <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "28px", fontWeight: 500, margin: 0 }}>
              <em>Stay connected</em>
            </h2>
            <p style={{ color: "#555", fontSize: "15px", marginTop: "12px", marginBottom: "26px", lineHeight: 1.6 }}>
              Sign up for updates on new acquisitions, exhibitions, and programming from MoDDA.
            </p>
            <form onSubmit={handleSubmit} style={{ textAlign: "left" }}>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
              />
              <input
                type="text"
                required
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "12px 14px",
                  fontSize: "15px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  marginBottom: "12px",
                  fontFamily: "inherit",
                }}
              />
              <input
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "12px 14px",
                  fontSize: "15px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  marginBottom: "16px",
                  fontFamily: "inherit",
                }}
              />
              {status === "error" && (
                <p style={{ color: "#b3261e", fontSize: "13px", marginTop: "-6px", marginBottom: "14px" }}>
                  {errorMessage}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "submitting"}
                style={{
                  width: "100%",
                  background: "#111",
                  color: "#fff",
                  border: "none",
                  borderRadius: "999px",
                  padding: "13px 16px",
                  fontSize: "14px",
                  letterSpacing: "0.02em",
                  cursor: status === "submitting" ? "default" : "pointer",
                  opacity: status === "submitting" ? 0.6 : 1,
                }}
              >
                {status === "submitting" ? "Signing up..." : "Sign up"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("General inquiry");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState(""); // honeypot, kept empty by real visitors
  const [renderedAt] = useState(() => Date.now());
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          reason,
          message,
          company,
          renderedAt,
          page: window.location.pathname,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Something went wrong");
      }
      setStatus("done");
    } catch (err: any) {
      setStatus("error");
      setErrorMessage(err && err.message ? err.message : "Something went wrong. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div style={{ marginTop: "28px" }}>
        <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "26px", fontWeight: 500, margin: 0 }}>
          <em>Thank you.</em>
        </h2>
        <p style={{ color: "#555", fontSize: "15px", marginTop: "12px", lineHeight: 1.6 }}>
          Your message has been received -- we'll be in touch soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: "28px", maxWidth: "480px" }}>
      <input
        type="text"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />
      <input
        type="text"
        required
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px 14px",
          fontSize: "15px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          marginBottom: "16px",
          fontFamily: "inherit",
        }}
      />
      <input
        type="email"
        required
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px 14px",
          fontSize: "15px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          marginBottom: "16px",
          fontFamily: "inherit",
        }}
      />
      <select
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px 14px",
          fontSize: "15px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          marginBottom: "16px",
          fontFamily: "inherit",
          background: "#fff",
        }}
      >
        <option>General inquiry</option>
        <option>Press</option>
        <option>Artist / collection submission</option>
        <option>Partnership</option>
        <option>Membership</option>
        <option>Other</option>
      </select>
      <textarea
        required
        placeholder="Message"
        rows={6}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px 14px",
          fontSize: "15px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          marginBottom: "16px",
          fontFamily: "inherit",
          resize: "vertical",
        }}
      />
      {status === "error" && (
        <p style={{ color: "#b3261e", fontSize: "13px", marginTop: "-8px", marginBottom: "14px" }}>
          {errorMessage}
        </p>
      )}
      <button
        type="submit"
        disabled={status === "submitting"}
        style={{
          background: "#111",
          color: "#fff",
          border: "none",
          borderRadius: "999px",
          padding: "13px 28px",
          fontSize: "14px",
          letterSpacing: "0.02em",
          cursor: status === "submitting" ? "default" : "pointer",
          opacity: status === "submitting" ? 0.6 : 1,
        }}
      >
        {status === "submitting" ? "Sending..." : "Send message"}
      </button>
    </form>
  );
}

function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState<Work | null>(null);
  const [featuredWorks, setFeaturedWorks] = useState<Work[]>(featuredSeeds);
  const [recentWorks, setRecentWorks] = useState<Work[]>(recentSeeds);
  const [timeBasedWorks, setTimeBasedWorks] = useState<Work[]>(timeBasedSeeds);
  const [portraitureWorks, setPortraitureWorks] = useState<Work[]>(portraitureSeeds);
  const [figurationWorks, setFigurationWorks] = useState<Work[]>(figurationSeeds);
  const [generativeWorks, setGenerativeWorks] = useState<Work[]>(generativeSeeds);
  const [photographyWorks, setPhotographyWorks] = useState<Work[]>(photographySeeds);
  const [collageWorks, setCollageWorks] = useState<Work[]>(collageSeeds);
  const [isMobile, setIsMobile] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cryptoDetailsOpen, setCryptoDetailsOpen] = useState(false);
  const [walletCopyLabel, setWalletCopyLabel] = useState("Copy wallet address");
useEffect(() => {
  const checkMobile = () => setIsMobile(window.innerWidth <= 768);
  checkMobile();
  window.addEventListener("resize", checkMobile);
  return () => window.removeEventListener("resize", checkMobile);
}, []);
useEffect(() => {
  setMobileMenuOpen(false);
  setDonateOpen(false);
  // Start every new page at the top (e.g. Donate -> Founding Circle used to open mid-page).
  // Proof of Culture manages its own scroll, so leave it alone there.
  if (!location.pathname.startsWith("/proof-of-culture")) window.scrollTo(0, 0);
}, [location.pathname]);
useEffect(() => {
  const pages: Record<string, { title: string; description: string }> = {
    "/": {
      title: "MoDDA | Museum of Diasporan Digital Art",
      description: "MoDDA is a living archive of Black cultural production — preserving digital work across portraiture, figuration, photography, generative systems, collage, and time-based media.",
    },
    "/about": {
      title: "About | MoDDA | Museum of Diasporan Digital Art",
      description: "MoDDA (Museum of Diasporan Digital Art) is a living archive of Black cultural production, preserving digital work across platforms, contexts, and systems of circulation.",
    },
    "/proof-of-culture": {
      title: "Proof of Culture | MoDDA | Museum of Diasporan Digital Art",
      description: "From 2020 through 2023, Black artists across the diaspora used digital tools to make art, tell stories, and build new spaces for their work. Proof of Culture is MoDDA's exhibition of that moment.",
    },
    "/manifesto": {
      title: "Manifesto | MoDDA",
      description: "The MoDDA manifesto: why the Museum of Diasporan Digital Art exists. Culture is not temporary. Digital does not mean permanent.",
    },
    "/collection": {
      title: "Selected Works | MoDDA | Museum of Diasporan Digital Art",
      description: "Browse selected works from MoDDA's collection — portraiture, figuration, photography, generative systems, collage, and time-based media by diasporan artists.",
    },
    "/team": {
      title: "Team | MoDDA | Museum of Diasporan Digital Art",
      description: "Meet the team, founding board, and advisory council behind MoDDA — the Museum of Diasporan Digital Art.",
    },
    "/donate": {
      title: "Support MoDDA | Museum of Diasporan Digital Art",
      description: "Support MoDDA's work preserving digital art and its history. Make a one-time or recurring gift, join the Founding Circle, or give in crypto.",
    },
    "/donate-art": {
      title: "Donate Artwork | MoDDA | Museum of Diasporan Digital Art",
      description: "Propose a donation of digital artwork to MoDDA. All proposals are subject to curatorial review and formal acceptance into the collection.",
    },
    "/founding-circle": {
      title: "The Founding Circle | MoDDA",
      description: "Join MoDDA's Founding Circle — a limited group of 20 individuals, families, and organizations whose early support helps establish the museum.",
    },
    "/contact": {
      title: "Contact | MoDDA | Museum of Diasporan Digital Art",
      description: "Contact MoDDA for inquiries related to the collection, partnerships, press, or artist conversations. Email: hello@modda.org",
    },
  };
  const meta = pages[location.pathname] || pages["/"];
  document.title = meta.title;
  const descTag = document.querySelector('meta[name="description"]');
  if (descTag) descTag.setAttribute("content", meta.description);
  const canonicalTag = document.querySelector('link[rel="canonical"]');
  if (canonicalTag) canonicalTag.setAttribute("href", `https://modda.org${location.pathname}`);
}, [location.pathname]);
  useEffect(() => {
    let active = true;
    async function loadWorks() {
      const [
        resolvedFeatured,
        resolvedRecent,
        resolvedTimeBased,
        resolvedPortraiture,
        resolvedFiguration,
        resolvedGenerative,
        resolvedPhotography,
        resolvedCollage,
      ] = await Promise.all([
        Promise.all(featuredSeeds.map(resolveWork)),
        Promise.all(recentSeeds.map(resolveWork)),
        Promise.all(timeBasedSeeds.map(resolveWork)),
        Promise.all(portraitureSeeds.map(resolveWork)),
        Promise.all(figurationSeeds.map(resolveWork)),
        Promise.all(generativeSeeds.map(resolveWork)),
        Promise.all(photographySeeds.map(resolveWork)),
        Promise.all(collageSeeds.map(resolveWork)),
      ]);
      if (active) {
        setFeaturedWorks(resolvedFeatured);
        setRecentWorks(resolvedRecent);
        setTimeBasedWorks(resolvedTimeBased);
        setPortraitureWorks(resolvedPortraiture);
        setFigurationWorks(resolvedFiguration);
        setGenerativeWorks(resolvedGenerative);
        setPhotographyWorks(resolvedPhotography);
        setCollageWorks(resolvedCollage);
      }
    }
    loadWorks();
    return () => {
      active = false;
    };
  }, []);
  const rooms = [
    { title: "Featured Works", items: featuredWorks },
    { title: "Recent Additions", items: recentWorks },
    { title: "Portraiture", items: portraitureWorks },
    { title: "Figuration", items: figurationWorks },
    { title: "Photography", items: photographyWorks },
    { title: "Time-Based", items: timeBasedWorks },
    { title: "Generative", items: generativeWorks },
    { title: "Collage & Mixed Media", items: collageWorks },
  ];
  function Row({ title, items }: { title: string; items: Work[] }) {
    const isFeatured = title === "Featured Works";
    return (
     <section
  style={{
    marginBottom: isMobile ? "44px" : isFeatured ? "118px" : "104px",
  }}
>
        <h2
          style={{
            padding: isMobile ? "0 24px" : "0 44px",
            marginBottom: isFeatured ? "34px" : "30px",
            fontFamily: "Cormorant Garamond, serif",
            fontSize: isFeatured ? "34px" : "30px",
            fontWeight: 400,
            letterSpacing: "0.01em",
          }}
        >
          {title}
        </h2>
        {sectionDescriptions[title] && (
          <p
            style={{
              padding: isMobile ? "0 24px" : "0 44px",
              margin: "-12px 0 30px",
              maxWidth: "820px",
              color: "#555",
              fontSize: "14px",
              lineHeight: 1.7,
            }}
          >
            {sectionDescriptions[title]}
          </p>
        )}
 <div
  style={{
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "42px" : isFeatured ? "36px" : "32px",
    overflowX: isMobile ? "visible" : "auto",
    overflowY: "visible",
    padding: isMobile ? "0 24px 12px" : "0 44px 40px",
  }}
>
          {items.map((work, i) => (
            <div
              key={`${title}-${work.artist}-${work.title}-${i}`}
              onClick={() => setSelected(work)}
             style={{
  width: isMobile ? "100%" : isFeatured ? "clamp(320px, 68vw, 520px)" : "clamp(300px, 68vw, 485px)",
  minWidth: isMobile ? "0" : isFeatured ? "clamp(320px, 68vw, 520px)" : "clamp(300px, 68vw, 485px)",
  flexShrink: 0,
  cursor: "pointer",
}}
            >
              <ArtworkImage work={work} isFeatured={isFeatured} />
              <div style={{ marginTop: "24px", lineHeight: 1.65 }}>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "19px", fontWeight: 500, letterSpacing: "0.01em" }}>
                  <em>{work.title}</em>{work.year ? `, ${work.year}` : ""}
                </div>
                <div style={{ fontFamily: "Cormorant Garamond, serif", color: "#666", fontSize: "16px", marginTop: "6px", letterSpacing: "0.01em" }}>
                  {work.artist}{work.country ? `, ${work.country}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  return (
<div
  style={{
    fontFamily: "Inter, Arial, sans-serif",
    background: "#fff",
    color: "#111",
    width: "100%",
    overflowX: isMobile ? "visible" : "hidden",
    display: "flex",
    flexDirection: "column",
  }}
>
      <header
        style={{
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? "20px 24px" : "28px 44px",
          borderBottom: "1px solid #eee",
          position: isMobile ? "sticky" : undefined,
          top: isMobile ? 0 : undefined,
          zIndex: isMobile ? 100 : undefined,
          background: isMobile ? "#fff" : undefined,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: isMobile ? "14px" : 0,
          }}
        >
          <div
            onClick={() => navigate("/")}
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontSize: isMobile ? "17px" : "18px",
              letterSpacing: "0.02em",
              cursor: "pointer",
              flex: isMobile ? "1 1 auto" : "none",
              minWidth: 0,
            }}
          >
            MODDA | Museum of Diasporan Digital Art
          </div>
          {isMobile ? (
            <button
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                width: "24px",
                height: "16px",
                position: "relative",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: mobileMenuOpen ? "7px" : "0px",
                  height: "1.5px",
                  background: "#111",
                  transition: "transform 0.2s ease, top 0.2s ease",
                  transform: mobileMenuOpen ? "rotate(45deg)" : "none",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: "7px",
                  height: "1.5px",
                  background: "#111",
                  opacity: mobileMenuOpen ? 0 : 1,
                  transition: "opacity 0.15s ease",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: mobileMenuOpen ? "7px" : "14px",
                  height: "1.5px",
                  background: "#111",
                  transition: "transform 0.2s ease, top 0.2s ease",
                  transform: mobileMenuOpen ? "rotate(-45deg)" : "none",
                }}
              />
            </button>
          ) : (
            <nav
              style={{
                display: "flex",
                gap: "28px",
                fontSize: "11px",
                color: "#666",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              <span onClick={() => navigate("/")}>home</span>
              {SHOW_POC_LINKS && <span onClick={() => navigate("/proof-of-culture")}>proof of culture</span>}
              <span onClick={() => navigate("/about")}>about</span>
              <span onClick={() => navigate("/manifesto")}>manifesto</span>
              <span onClick={() => navigate("/collection")}>selected works</span>
              <span
                style={{ position: "relative", display: "inline-block" }}
                onMouseEnter={() => setDonateOpen(true)}
                onMouseLeave={() => setDonateOpen(false)}
              >
                <span onClick={() => setDonateOpen((open) => !open)}>donate</span>
                {donateOpen && (
                  <span
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      paddingTop: "10px",
                      display: "block",
                      zIndex: 20,
                      textTransform: "none",
                      letterSpacing: "normal",
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        background: "#fff",
                        border: "1px solid #eee",
                        borderRadius: "4px",
                        padding: "6px 0",
                        minWidth: "170px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      }}
                    >
                      <span
                        onClick={() => { navigate("/donate"); setDonateOpen(false); }}
                        style={{ display: "block", padding: "10px 16px", color: "#666", fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap", cursor: "pointer" }}
                      >
                        Support MoDDA
                      </span>
                      <span
                        onClick={() => { navigate("/founding-circle"); setDonateOpen(false); }}
                        style={{ display: "block", padding: "10px 16px", color: "#666", fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap", cursor: "pointer" }}
                      >
                        Founding Circle
                      </span>
                      <span
                        onClick={() => { navigate("/donate-art"); setDonateOpen(false); }}
                        style={{ display: "block", padding: "10px 16px", color: "#666", fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap", cursor: "pointer" }}
                      >
                        Donate Art
                      </span>
                    </span>
                  </span>
                )}
              </span>
              <span onClick={() => navigate("/team")}>team</span>
              <span onClick={() => navigate("/contact")}>contact</span>
            </nav>
          )}
        </div>

        {isMobile && mobileMenuOpen && (
          <nav
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              fontSize: "13px",
              color: "#666",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              cursor: "pointer",
              marginTop: "20px",
            }}
          >
            <span onClick={() => { navigate("/"); setMobileMenuOpen(false); }}>home</span>
            {SHOW_POC_LINKS && <span onClick={() => { navigate("/proof-of-culture"); setMobileMenuOpen(false); }}>proof of culture</span>}
            <span onClick={() => { navigate("/about"); setMobileMenuOpen(false); }}>about</span>
            <span onClick={() => { navigate("/manifesto"); setMobileMenuOpen(false); }}>manifesto</span>
            <span onClick={() => { navigate("/collection"); setMobileMenuOpen(false); }}>selected works</span>
            <span>
              <span onClick={() => setDonateOpen((open) => !open)}>donate</span>
              {donateOpen && (
                <span
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    paddingTop: "16px",
                    paddingLeft: "16px",
                    textTransform: "none",
                    letterSpacing: "normal",
                  }}
                >
                  <span
                    onClick={() => { navigate("/donate"); setDonateOpen(false); setMobileMenuOpen(false); }}
                    style={{ color: "#666", fontSize: "13px", letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
                  >
                    Support MoDDA
                  </span>
                  <span
                    onClick={() => { navigate("/founding-circle"); setDonateOpen(false); setMobileMenuOpen(false); }}
                    style={{ color: "#666", fontSize: "13px", letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
                  >
                    Founding Circle
                  </span>
                  <span
                    onClick={() => { navigate("/donate-art"); setDonateOpen(false); setMobileMenuOpen(false); }}
                    style={{ color: "#666", fontSize: "13px", letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
                  >
                    Donate Art
                  </span>
                </span>
              )}
            </span>
            <span onClick={() => { navigate("/team"); setMobileMenuOpen(false); }}>team</span>
            <span onClick={() => { navigate("/contact"); setMobileMenuOpen(false); }}>contact</span>
          </nav>
        )}
      </header>
      <Routes>
        <Route path="/" element={
          <main>
            <section style={{ padding: isMobile ? "56px 24px 32px" : "96px 44px 40px" }}>
              <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(48px, 7vw, 104px)", lineHeight: 0.94, fontWeight: 400, maxWidth: "1100px", margin: 0 }}>
                A <em>home</em> for Black digital art.<br />A <em>record</em> for the future.
              </h1>
              <p style={{ marginTop: "28px", maxWidth: "640px", color: "#444", fontSize: "17px", lineHeight: 1.7 }}>
                MoDDA is a digital-first museum dedicated to collecting, preserving, researching, and exhibiting digital art from the Black diaspora.
              </p>
              {SHOW_POC_LINKS && (
                <button
                  onClick={() => navigate("/proof-of-culture")}
                  style={{
                    marginTop: isMobile ? "28px" : "40px",
                    background: "#111",
                    color: "#fff",
                    border: "none",
                    borderRadius: "999px",
                    padding: isMobile ? "12px 22px" : "14px 28px",
                    fontSize: "12px",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  Enter Proof of Culture →
                </button>
              )}
            </section>
            <Row title="Featured Works" items={featuredWorks} />
            <Row title="Recent Additions" items={recentWorks} />
            <section style={{ padding: isMobile ? "0 24px 12px" : "0 44px 12px" }}>
              <div style={{ borderTop: "1px solid #eee", paddingTop: "24px", maxWidth: "620px", color: "#555", fontSize: "14px", lineHeight: 1.7 }}>
                <span style={{ textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "11px", color: "#777" }}>Forthcoming Exhibitions</span>
                <div style={{ marginTop: "8px" }}>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "26px", color: "#111" }}><em>Proof of Culture</em></div>
                  <p style={{ margin: "2px 0 2px" }}><em>Works from a transformative era in Black digital art, 2020–2023.</em></p>
                  <p style={{ margin: "0 0 12px" }}>Opening October 15th.</p>
                </div>
                <div>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "26px", color: "#111" }}><em>Machine Mediations</em></div>
                  <p style={{ margin: "2px 0 0" }}>On artists working with and against the logic of machines.</p>
                </div>
              </div>
            </section>
          </main>
        } />

        <Route path="/museum" element={
          <MuseumWalkthrough
            rooms={rooms.map((room) => ({ ...room, description: sectionDescriptions[room.title] }))}
          />
        } />

        <Route path="/proof-of-culture" element={<ProofOfCultureLine />} />

        <Route path="/collection" element={
          <main>
            <section style={{ padding: isMobile ? "56px 24px 48px" : "96px 44px 72px" }}>
              <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "72px", fontWeight: 400, margin: 0 }}>
                Selected Works
              </h1>
            </section>
            {rooms.slice(2).map((room) => (
              <Row key={room.title} title={room.title} items={room.items} />
            ))}
          </main>
        } />
        <Route path="/about" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 44px", maxWidth: "860px", lineHeight: 1.85 }}>
            <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "72px", fontWeight: 400, marginBottom: "34px" }}>About</h1>
            <p>MoDDA (Museum of Diasporan Digital Art) is a living archive of Black cultural production. It emerges from an ongoing collecting practice focused on preserving digital work across platforms, contexts, and systems of circulation.</p>
            <p>The works gathered here span portraiture, figuration, photography, generative systems, collage, and time-based media. Together, they trace how artists across the diaspora use digital tools to construct identity, document lived experience, and imagine new visual languages. Many of these works were created for platforms designed for speed and visibility, where images circulate rapidly and disappear just as quickly. MoDDA resists that logic by creating a space for sustained attention.</p>
            <p>The archive is both personal and public. It reflects the perspective of its founder while operating as an evolving record of a broader cultural moment. Rather than organizing itself around rigid taxonomies, MoDDA presents the collection through curatorial groupings that emphasize resonance, relation, and context.</p>
            <p>At its core, MoDDA is an argument: that digital art within the Black diaspora is not peripheral to contemporary art history, but central to it. By preserving, revisiting, and reframing these works, the archive contributes to a longer timeline of cultural memory, one that extends beyond the platforms on which the work first appeared.</p>
          </main>
        } />
        <Route path="/manifesto" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 44px", maxWidth: "860px", lineHeight: 1.85 }}>
            <h1 style={{ fontFamily: "EB Garamond, serif", fontSize: isMobile ? "48px" : "72px", fontWeight: 400, marginBottom: "34px" }}>The MoDDA Manifesto</h1>
            <p style={{ fontFamily: "EB Garamond, serif", fontSize: isMobile ? "24px" : "28px", fontStyle: "italic", lineHeight: 1.5, marginBottom: "36px" }}>Culture is not temporary.</p>
            <p>Yet the systems built to preserve culture have never treated all cultures equally. Museums determine what is worthy of collecting, archives decide what is worthy of remembering and markets determine what is worthy of protecting. While all this is happening, entire communities are watching their histories disappear because no institution believed they mattered enough to preserve.</p>
            <p>Today, history is disappearing for a different reason. Platforms are shutting down, links are breaking and servers are disappearing. Metadata vanishes right before our eyes and with little or no warning. What was once stored in vaults is now stored on servers owned by companies whose priorities can change overnight.</p>
            <p>For Black diasporic artists, this vulnerability is not new. Our cultural production has always existed closer to the margins than the center. Again and again, we have been asked to prove our significance before anyone considers preserving our work. We are being asked to audition for our own history.</p>
            <p style={{ fontWeight: 700 }}>MoDDA rejects that premise. Full stop.</p>
            <div style={{ maxWidth: "420px", margin: "44px auto" }}>
              <img
                src="/media/iamstillhere.webp"
                alt="I am still here by Janice Mascarenhas, 2024"
                style={{ width: "100%", borderRadius: "2px", display: "block" }}
              />
              <p style={{ fontSize: "12px", color: "#888", fontStyle: "italic", marginTop: "12px" }}>
                &ldquo;I am still here&rdquo; by Janice Mascarenhas, 2024
              </p>
            </div>
            <p>The work is valuable because it exists. It deserves documentation before validation, preservation before speculation and scholarship before scarcity.</p>
            <p style={{ fontStyle: "italic" }}>Name the institution that saved our work the last time the platforms went dark. We'll wait.</p>
            <p>MoDDA exists because waiting has never been a survival strategy for us and we truly believe that preservation is an act of cultural care. Care in documentation, in scholarship and in preserving metadata is memory. Because after all, a work without its story is an incomplete work and an archive without its people is just an empty vessel.</p>
            <p>So we are building the whole damn architecture: infrastructure, not simply collections, relationships, not merely databases and creating real evidence that this moment existed.</p>
            <p>Our work is in preparation so that future historians do not have to reconstruct this movement from screenshots and broken links. They should inherit a record and that cultural record should be built by those who understand its significance, not only by those who inherit the power to define it.</p>
            <p>This is an act of remembrance, of resistance and care.</p>
            <p style={{ fontFamily: "EB Garamond, serif", fontSize: isMobile ? "34px" : "42px", fontStyle: "italic", marginTop: "44px", lineHeight: 1.4 }}>This is MoDDA.</p>
          </main>
        } />
        <Route path="/team" element={
          <main>
            <section style={{ padding: isMobile ? "56px 24px 32px" : "96px 44px 40px" }}>
              <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "72px", fontWeight: 400, margin: 0 }}>
                Team
              </h1>
              <p style={{ marginTop: "24px", maxWidth: "640px", color: "#444", fontSize: "17px", lineHeight: 1.7 }}>
                The people building MoDDA's collection, governance, and institutional practice.
              </p>
            </section>
            {/* Founder */}
            <section style={{ padding: isMobile ? "0 24px 56px" : "0 44px 96px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? "28px" : "56px",
                  alignItems: "flex-start",
                }}
              >
                <img
                  src={founder.photo}
                  alt={founder.name}
                  loading="lazy"
                  style={{
                    width: isMobile ? "160px" : "240px",
                    minWidth: isMobile ? "0" : "240px",
                    aspectRatio: "4 / 5",
                    objectFit: "cover",
                    objectPosition: "center 20%",
                    display: "block",
                    borderRadius: "6px",
                    backgroundColor: "#f1f1f1",
                  }}
                />
                <div style={{ lineHeight: 1.8 }}>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "32px" : "40px" }}>
                    {founder.name}
                  </div>
                  <div style={{ color: "#666", fontSize: "13px", marginTop: "8px", marginBottom: "20px", letterSpacing: "0.02em" }}>
                    {founder.role}
                  </div>
                  <p style={{ color: "#444", fontSize: "15px", maxWidth: "680px" }}>{founder.bio}</p>
                </div>
              </div>
            </section>
            <TeamSection title="Founding Board" members={isMobile ? foundingBoardMobile : foundingBoard} isMobile={isMobile} />
            <TeamSection title="Advisory Council" members={advisoryCouncil} isMobile={isMobile} columns={7} />
          </main>
        } />
        <Route path="/donate" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 64px", maxWidth: "760px" }}>
            <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "76px", fontWeight: 400, lineHeight: 1.02, marginBottom: isMobile ? "28px" : "36px" }}>
              Support MoDDA
            </h1>

            <div style={{ lineHeight: 1.85, color: "#222", fontSize: "17px", marginBottom: isMobile ? "40px" : "56px" }}>
              <p>Digital art needs more than a place to be seen. It needs people committed to keeping the work, its history, and the stories around it accessible over time.</p>
              <p>Your support helps MoDDA preserve digital works and their records, care for the collection, conduct research, and create exhibitions and public programs that bring the art of the Black diaspora into wider view.</p>
            </div>

            <div style={{ marginBottom: isMobile ? "48px" : "72px" }}>
              <img
                src="/media/h0piummm-its-complicated.jpg"
                alt="It's Complicated by H0piummm, 2024"
                loading="lazy"
                style={{ width: "100%", borderRadius: "4px", backgroundColor: "#f5f5f5", display: "block" }}
              />
              <p style={{ fontSize: "12px", color: "#888", fontStyle: "italic", marginTop: "14px" }}>
                &ldquo;It&rsquo;s Complicated&rdquo; by H0piummm, 2024 &mdash; from MoDDA&rsquo;s collection.
              </p>
            </div>

            {/* Make a donation */}
            <div style={{ borderTop: "1px solid #111", paddingTop: isMobile ? "28px" : "36px", marginBottom: isMobile ? "48px" : "72px" }}>
              <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "38px", fontWeight: 500, marginBottom: "16px" }}>
                Make a donation
              </h2>
              <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px", marginBottom: "24px", maxWidth: "520px" }}>
                Give an amount that works for you. One-time and recurring gifts help sustain the museum&rsquo;s work.
              </p>
              <a
                href="https://fundraising.fracturedatlas.org/museum-of-diasporan-digital-art"
                target="_blank"
                rel="noreferrer"
                style={{ display: "inline-block", background: "#111", color: "#fff", border: "none", borderRadius: "999px", padding: isMobile ? "17px 32px" : "18px 38px", fontSize: "13px", letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", cursor: "pointer" }}
              >
                Make a donation
              </a>
            </div>

            {/* Founding Circle teaser */}
            <div style={{ borderTop: "1px solid #eee", paddingTop: isMobile ? "28px" : "36px", marginBottom: isMobile ? "48px" : "72px" }}>
              <span style={{ display: "block", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "11px", color: "#999", marginBottom: "12px" }}>
                Limited to 20 members
              </span>
              <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "38px", fontWeight: 500, marginBottom: "16px" }}>
                Join the Founding Circle
              </h2>
              <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px", marginBottom: "16px", maxWidth: "560px" }}>
                The Founding Circle is a group of early supporters helping build MoDDA&rsquo;s foundation. Members contribute $5,000 or more and receive permanent recognition, private briefings, early access to exhibitions and programs, and a limited Founding Edition print by Bhare.
              </p>
              <span
                onClick={() => navigate("/founding-circle")}
                style={{ display: "inline-block", color: "#111", fontSize: "15px", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer", marginBottom: "28px" }}
              >
                Read more about the Founding Circle
              </span>
              <div>
                <a
                  href="https://fundraising.fracturedatlas.org/museum-of-diasporan-digital-art/general_support"
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-block", background: "#fff", color: "#111", border: "1px solid #111", borderRadius: "999px", padding: isMobile ? "16px 30px" : "17px 36px", fontSize: "13px", letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", cursor: "pointer" }}
                >
                  Join the Founding Circle &mdash; Give $5,000 or more
                </a>
              </div>
            </div>

            {/* Give in crypto */}
            <div style={{ borderTop: "1px solid #eee", paddingTop: isMobile ? "28px" : "36px", marginBottom: isMobile ? "40px" : "56px" }}>
              <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "38px", fontWeight: 500, marginBottom: "16px" }}>
                Give in crypto
              </h2>
              <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px", marginBottom: "16px", maxWidth: "560px" }}>
                MoDDA also accepts ETH and USDC at donate.modda.eth. Crypto gifts made directly to this wallet are separate from donations through Fractured Atlas and are not tax-deductible.
              </p>
              <span
                onClick={() => setCryptoDetailsOpen((open) => !open)}
                style={{ display: "inline-block", color: "#666", fontSize: "14px", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" }}
              >
                {cryptoDetailsOpen ? "Hide crypto giving details" : "View crypto giving details"}
              </span>

              {cryptoDetailsOpen && (
                <div style={{ marginTop: "20px", paddingLeft: isMobile ? "0" : "20px", borderLeft: isMobile ? "none" : "2px solid #eee", maxWidth: "560px" }}>
                  <p style={{ color: "#444", lineHeight: 1.75, fontSize: "15px", marginBottom: "14px" }}>
                    MoDDA accepts ETH and USDC on Ethereum mainnet at donate.modda.eth.
                  </p>
                  <p style={{ color: "#444", lineHeight: 1.75, fontSize: "15px", marginBottom: "14px" }}>
                    Please confirm that your wallet shows Ethereum mainnet before sending. Transfers made on other networks may not reach MoDDA.
                  </p>
                  <p style={{ color: "#444", lineHeight: 1.75, fontSize: "15px", marginBottom: "20px" }}>
                    Direct crypto gifts do not go through Fractured Atlas and are not tax-deductible. If you&rsquo;d like us to acknowledge your gift,{" "}
                    <span onClick={() => navigate("/contact")} style={{ color: "#111", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" }}>
                      contact us
                    </span>{" "}
                    with your transaction hash and the name you&rsquo;d like us to use.
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText("donate.modda.eth");
                        setWalletCopyLabel("Copied");
                        setTimeout(() => setWalletCopyLabel("Copy wallet address"), 2000);
                      } catch {
                        setWalletCopyLabel("Copy failed — donate.modda.eth");
                      }
                    }}
                    style={{ display: "inline-block", background: "#fff", color: "#111", border: "1px solid #111", borderRadius: "999px", padding: "12px 24px", fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
                  >
                    {walletCopyLabel}
                  </button>
                </div>
              )}
            </div>

            <div style={{ paddingTop: "24px", borderTop: "1px solid #eee", fontSize: "11px", color: "#999", lineHeight: 1.7 }}>
              <p>MoDDA is fiscally sponsored by Fractured Atlas. Donations made through Fractured Atlas are tax-deductible to the extent permitted by law.</p>
            </div>
          </main>
        } />

        <Route path="/donate-art" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 64px", maxWidth: "760px" }}>
            <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "76px", fontWeight: 400, lineHeight: 1.02, marginBottom: isMobile ? "28px" : "36px" }}>
              Donate Artwork to MoDDA
            </h1>

            <div style={{ lineHeight: 1.85, color: "#222", fontSize: "17px", marginBottom: isMobile ? "48px" : "64px" }}>
              <p>MoDDA welcomes proposals for donations of digital artwork that align with our mission to collect, preserve, and exhibit the digital art of the Black diaspora.</p>
              <p>Through the generosity of artists and collectors, we continue to build a collection that reflects the breadth of Black artistic expression in digital spaces.</p>
            </div>

            {/* How to donate */}
            <div style={{ borderTop: "1px solid #111", paddingTop: isMobile ? "28px" : "36px", marginBottom: isMobile ? "48px" : "72px" }}>
              <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "38px", fontWeight: 500, marginBottom: "12px" }}>
                How to Donate Artwork
              </h2>
              <div style={{ display: "flex", gap: isMobile ? "16px" : "24px", padding: "22px 0", borderTop: "1px solid #eee" }}>
                <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "36px", lineHeight: 1, color: "#999", minWidth: isMobile ? "24px" : "32px" }}>1</span>
                <div>
                  <h3 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "24px" : "28px", fontWeight: 500, margin: "0 0 8px" }}>Submit a Proposal</h3>
                  <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px", margin: 0 }}>Complete the artwork donation form with information about the artist, work, ownership, and provenance.</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: isMobile ? "16px" : "24px", padding: "22px 0", borderTop: "1px solid #eee" }}>
                <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "36px", lineHeight: 1, color: "#999", minWidth: isMobile ? "24px" : "32px" }}>2</span>
                <div>
                  <h3 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "24px" : "28px", fontWeight: 500, margin: "0 0 8px" }}>Curatorial Review</h3>
                  <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px", margin: 0 }}>MoDDA reviews each proposal for its relevance to the collection, provenance, and preservation considerations.</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: isMobile ? "16px" : "24px", padding: "22px 0", borderTop: "1px solid #eee" }}>
                <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "36px", lineHeight: 1, color: "#999", minWidth: isMobile ? "24px" : "32px" }}>3</span>
                <div>
                  <h3 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "24px" : "28px", fontWeight: 500, margin: "0 0 8px" }}>Transfer and Acceptance</h3>
                  <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px", margin: 0 }}>If a proposal is approved for the next stage, MoDDA will provide instructions for transferring the artwork to a designated intake wallet. Following verification and formal acceptance, the work will be accessioned into the permanent collection.</p>
                </div>
              </div>
            </div>

            {/* Propose a donation */}
            <div style={{ borderTop: "1px solid #111", paddingTop: isMobile ? "28px" : "36px", marginBottom: isMobile ? "48px" : "72px" }}>
              <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "30px" : "38px", fontWeight: 500, marginBottom: "16px" }}>
                Propose an Artwork Donation
              </h2>
              <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px", marginBottom: "24px", maxWidth: "560px" }}>
                We welcome submissions from artists, collectors, and other authorized owners.
              </p>
              <a
                href={ART_DONATION_FORM_URL}
                target={ART_DONATION_FORM_URL.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                style={{ display: "inline-block", background: "#111", color: "#fff", border: "none", borderRadius: "999px", padding: isMobile ? "17px 32px" : "18px 38px", fontSize: "13px", letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", cursor: "pointer" }}
              >
                Submit Artwork Proposal
              </a>
            </div>

            <div style={{ paddingTop: "24px", borderTop: "1px solid #eee", fontSize: "12px", color: "#888", lineHeight: 1.7, maxWidth: "620px" }}>
              <p>All artwork donations are subject to curatorial review and formal acceptance. Submission of a proposal or unsolicited transfer of an artwork does not constitute acceptance into MoDDA&rsquo;s collection. Please do not transfer artwork before receiving instructions from MoDDA.</p>
            </div>
          </main>
        } />
        <Route path="/founding-circle" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 64px", maxWidth: "1240px" }}>
            <span style={{ display: "block", textTransform: "uppercase", letterSpacing: "0.14em", fontSize: "11px", color: "#999", marginBottom: "18px" }}>
              Limited to 20 members
            </span>
            <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "84px", fontWeight: 400, lineHeight: 1.02, marginBottom: isMobile ? "40px" : "64px", maxWidth: "780px" }}>
              The Founding Circle
            </h1>

            {/* Opening section — artwork on one side, intro copy + contribution level on the other */}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "40px" : "80px", alignItems: "flex-start", marginBottom: isMobile ? "64px" : "96px" }}>

              {/* Artwork column */}
              <div style={{ flex: isMobile ? "none" : "0 0 48%", width: isMobile ? "100%" : "48%" }}>
                <img
                  src="/media/bhare-founding-edition.png"
                  alt="Founding Edition print by Bhare, created exclusively for MoDDA's Founding Circle"
                  loading="lazy"
                  style={{ width: "100%", borderRadius: "4px", backgroundColor: "#f5f5f5" }}
                />
                <p style={{ fontSize: "12px", color: "#888", fontStyle: "italic", marginTop: "14px" }}>
                  Founding Edition print by Bhare — signed and numbered, never exceeding 20.
                </p>

              </div>

              {/* Intro + contribution column */}
              <div style={{ flex: isMobile ? "none" : "1 1 auto", width: isMobile ? "100%" : "auto", paddingTop: isMobile ? "0" : "12px" }}>
                <div style={{ lineHeight: 1.85, color: "#222", fontSize: "17px", marginBottom: isMobile ? "36px" : "52px", maxWidth: "460px" }}>
                  <p>The MoDDA Founding Circle is limited to 20 individuals, families, and organizations making contributions of $5,000 or more.</p>
                  <p>Founding Circle members will be permanently recognized as part of the group whose early support helped establish the museum. Members may choose to be listed by name, family name, organization, or anonymously.</p>
                </div>

                <div style={{ borderTop: "1px solid #111", paddingTop: isMobile ? "28px" : "36px", maxWidth: "460px" }}>
                  <span style={{ display: "block", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "11px", color: "#777", marginBottom: "12px" }}>
                    Founding contribution
                  </span>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: isMobile ? "56px" : "76px", lineHeight: 1, color: "#111" }}>
                    $5,000<span style={{ fontSize: isMobile ? "20px" : "26px", fontStyle: "normal", color: "#777" }}> and above</span>
                  </div>
                  <p style={{ fontSize: "14px", color: "#666", marginTop: "16px", marginBottom: isMobile ? "28px" : "36px" }}>
                    Founding Circle recognition and benefits are the same for every member, regardless of contribution amount.
                  </p>

                  <a href="https://fundraising.fracturedatlas.org/museum-of-diasporan-digital-art/general_support" target="_blank" rel="noreferrer" style={{ display: "inline-block", background: "#111", color: "#fff", border: "none", borderRadius: "999px", padding: isMobile ? "17px 32px" : "20px 46px", fontSize: "13px", letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", cursor: "pointer" }}>
                    Join the Founding Circle
                  </a>
                  <p style={{ fontSize: "13px", color: "#999", marginTop: "16px" }}>
                    Prefer to contribute in crypto? Contributions can also be made to donate.modda.eth.
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits — five equal statements, three over two, each carrying its own understated line icon */}
            <div style={{ marginBottom: isMobile ? "64px" : "96px" }}>
              <span style={{ display: "block", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "11px", color: "#777", marginBottom: isMobile ? "32px" : "48px" }}>
                Members will receive
              </span>

              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "center", gap: isMobile ? "36px" : "56px 64px" }}>

                {/* I — Recognition */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <rect x="3.5" y="6.5" width="17" height="11" rx="1" />
                    <line x1="7" y1="10" x2="17" y2="10" />
                    <line x1="7" y1="13.5" x2="13" y2="13.5" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>Permanent recognition on MoDDA&rsquo;s Founding Circle page</p>
                </div>

                {/* II — Gathering */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <circle cx="8" cy="9" r="2.3" />
                    <circle cx="16" cy="9" r="2.3" />
                    <circle cx="12" cy="16" r="2.3" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>An invitation to an annual virtual Founding Circle gathering with MoDDA&rsquo;s founder, members of its leadership, and invited artists or curators</p>
                </div>

                {/* III — Preview access */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <path d="M2 12C4.5 7.5 8 5.5 12 5.5C16 5.5 19.5 7.5 22 12C19.5 16.5 16 18.5 12 18.5C8 18.5 4.5 16.5 2 12Z" />
                    <circle cx="12" cy="12" r="2.6" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>Private preview access to major MoDDA exhibitions before they open to the public</p>
                </div>

                {/* IV — Updates */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <rect x="3" y="6" width="18" height="12" rx="1" />
                    <path d="M4 7.5L12 13L20 7.5" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>Two annual Founding Circle updates sharing progress across preservation, acquisitions, exhibitions, research, and institutional development</p>
                </div>

                {/* V — Framed artwork (Founding Edition print) */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <rect x="4" y="4" width="16" height="16" rx="0.5" />
                    <rect x="7.5" y="7.5" width="9" height="9" rx="0.5" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>A signed and numbered Founding Edition print by artist Bhare, accompanied by a certificate of authenticity</p>
                </div>

              </div>
            </div>

            {/* Closing statement */}
            <p style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: isMobile ? "26px" : "36px", lineHeight: 1.4, color: "#111", maxWidth: "780px", marginBottom: isMobile ? "56px" : "80px" }}>
              The Founding Edition is being created exclusively for MoDDA&rsquo;s Founding Circle and will never exceed 20 prints.
            </p>

            <div style={{ paddingTop: "24px", borderTop: "1px solid #eee", fontSize: "11px", color: "#999", lineHeight: 1.7 }}>
              <p>Tax-deductible contributions to MoDDA are made through Fractured Atlas.</p>
              <p style={{ marginTop: "6px" }}>Cryptocurrency contributions are made directly to MoDDA and are not tax-deductible through Fractured Atlas.</p>
            </div>
          </main>
        } />

        <Route path="/manifesto" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 44px", maxWidth: "860px", lineHeight: 1.85 }}>
            <h1 style={{ fontFamily: "EB Garamond, serif", fontSize: isMobile ? "48px" : "72px", fontWeight: 400, marginBottom: "34px" }}>The MoDDA Manifesto</h1>

            <p style={{ fontFamily: "EB Garamond, serif", fontSize: isMobile ? "24px" : "28px", fontStyle: "italic", lineHeight: 1.5, marginBottom: "36px" }}>Culture is not temporary.</p>

            <p>Yet the systems built to preserve culture have never treated all cultures equally. Museums determine what is worthy of collecting, archives decide what is worthy of remembering and markets determine what is worthy of protecting. While all this is happening, entire communities are watching their histories disappear because no institution believed they mattered enough to preserve.</p>

            <p>Today, history is disappearing for a different reason. Platforms are shutting down, links are breaking and servers are disappearing. Metadata vanishes right before our eyes and with little or no warning. What was once stored in vaults is now stored on servers owned by companies whose priorities can change overnight.</p>

            <p>For Black diasporic artists, this vulnerability is not new. Our cultural production has always existed closer to the margins than the center. Again and again, we have been asked to prove our significance before anyone considers preserving our work. We are being asked to audition for our own history.</p>

            <p style={{ fontWeight: 700 }}>MoDDA rejects that premise. Full stop.</p>

            <p>The work is valuable because it exists. It deserves documentation before validation, preservation before speculation and scholarship before scarcity.</p>

            <p style={{ fontStyle: "italic" }}>Name the institution that saved our work the last time the platforms went dark. We'll wait.</p>

            <p>MoDDA exists because waiting has never been a survival strategy for us and we truly believe that preservation is an act of cultural care. Care in documentation, in scholarship and in preserving metadata is memory. Because after all, a work without its story is an incomplete work and an archive without its people is just an empty vessel.</p>

            <p>So we are building the whole damn architecture: infrastructure, not simply collections, relationships, not merely databases and creating real evidence that this moment existed.</p>

            <p>Our work is in preparation so that future historians do not have to reconstruct this movement from screenshots and broken links. They should inherit a record and that cultural record should be built by those who understand its significance, not only by those who inherit the power to define it.</p>

            <p>This is an act of remembrance, of resistance and care.</p>

            <p style={{ fontFamily: "EB Garamond, serif", fontSize: isMobile ? "34px" : "42px", fontStyle: "italic", marginTop: "44px", lineHeight: 1.4 }}>This is MoDDA.</p>
          </main>
        } />

        <Route path="/founding-circle" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 64px", maxWidth: "1240px" }}>
            <span style={{ display: "block", textTransform: "uppercase", letterSpacing: "0.14em", fontSize: "11px", color: "#999", marginBottom: "18px" }}>
              Limited to 20 members
            </span>
            <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "84px", fontWeight: 400, lineHeight: 1.02, marginBottom: isMobile ? "40px" : "64px", maxWidth: "780px" }}>
              The Founding Circle
            </h1>

            {/* Opening section — artwork on one side, intro copy + contribution level on the other */}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "40px" : "80px", alignItems: "flex-start", marginBottom: isMobile ? "64px" : "96px" }}>

              {/* Artwork column */}
              <div style={{ flex: isMobile ? "none" : "0 0 48%", width: isMobile ? "100%" : "48%" }}>
                <img
                  src="/media/founding-circle/bhare-founding-edition.png"
                  alt="Founding Edition print by Bhare, created exclusively for MoDDA's Founding Circle"
                  style={{ width: "100%", borderRadius: "4px", backgroundColor: "#f5f5f5" }}
                />
                <p style={{ fontSize: "12px", color: "#888", fontStyle: "italic", marginTop: "14px" }}>
                  Founding Edition print by Bhare — signed and numbered, never exceeding 20.
                </p>

              </div>

              {/* Intro + contribution column */}
              <div style={{ flex: isMobile ? "none" : "1 1 auto", width: isMobile ? "100%" : "auto", paddingTop: isMobile ? "0" : "12px" }}>
                <div style={{ lineHeight: 1.85, color: "#222", fontSize: "17px", marginBottom: isMobile ? "36px" : "52px", maxWidth: "460px" }}>
                  <p>The MoDDA Founding Circle is limited to 20 individuals, families, and organizations making contributions of $5,000 or more.</p>
                  <p>Founding Circle members will be permanently recognized as part of the group whose early support helped establish the museum. Members may choose to be listed by name, family name, organization, or anonymously.</p>
                </div>

                <div style={{ borderTop: "1px solid #111", paddingTop: isMobile ? "28px" : "36px", maxWidth: "460px" }}>
                  <span style={{ display: "block", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "11px", color: "#777", marginBottom: "12px" }}>
                    Founding contribution
                  </span>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: isMobile ? "56px" : "76px", lineHeight: 1, color: "#111" }}>
                    $5,000<span style={{ fontSize: isMobile ? "20px" : "26px", fontStyle: "normal", color: "#777" }}> and above</span>
                  </div>
                  <p style={{ fontSize: "14px", color: "#666", marginTop: "16px", marginBottom: isMobile ? "28px" : "36px" }}>
                    Founding Circle recognition and benefits are the same for every member, regardless of contribution amount.
                  </p>

                  <a
                    href="https://fundraising.fracturedatlas.org/museum-of-diasporan-digital-art"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-block",
                      background: "#111",
                      color: "#fff",
                      border: "none",
                      borderRadius: "999px",
                      padding: isMobile ? "17px 32px" : "20px 46px",
                      fontSize: "13px",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    Join the Founding Circle
                  </a>
                  <p style={{ fontSize: "13px", color: "#999", marginTop: "16px" }}>
                    Prefer to contribute in crypto? Contributions can also be made to donate.modda.eth.
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits — five equal statements, three over two, each carrying its own understated line icon */}
            <div style={{ marginBottom: isMobile ? "64px" : "96px" }}>
              <span style={{ display: "block", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "11px", color: "#777", marginBottom: isMobile ? "32px" : "48px" }}>
                Members will receive
              </span>

              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "center", gap: isMobile ? "36px" : "56px 64px" }}>

                {/* I — Recognition */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <rect x="3.5" y="6.5" width="17" height="11" rx="1" />
                    <line x1="7" y1="10" x2="17" y2="10" />
                    <line x1="7" y1="13.5" x2="13" y2="13.5" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>Permanent recognition on MoDDA&rsquo;s Founding Circle page</p>
                </div>

                {/* II — Gathering */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <circle cx="8" cy="9" r="2.3" />
                    <circle cx="16" cy="9" r="2.3" />
                    <circle cx="12" cy="16" r="2.3" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>An invitation to an annual virtual Founding Circle gathering with MoDDA&rsquo;s founder, members of its leadership, and invited artists or curators</p>
                </div>

                {/* III — Preview access */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <path d="M2 12C4.5 7.5 8 5.5 12 5.5C16 5.5 19.5 7.5 22 12C19.5 16.5 16 18.5 12 18.5C8 18.5 4.5 16.5 2 12Z" />
                    <circle cx="12" cy="12" r="2.6" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>Private preview access to major MoDDA exhibitions before they open to the public</p>
                </div>

                {/* IV — Updates */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <rect x="3" y="6" width="18" height="12" rx="1" />
                    <path d="M4 7.5L12 13L20 7.5" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>Two annual Founding Circle updates sharing progress across preservation, acquisitions, exhibitions, research, and institutional development</p>
                </div>

                {/* V — Framed artwork (Founding Edition print) */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: isMobile ? "1 1 100%" : "0 1 320px", maxWidth: isMobile ? "100%" : "340px" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                    <rect x="4" y="4" width="16" height="16" rx="0.5" />
                    <rect x="7.5" y="7.5" width="9" height="9" rx="0.5" />
                  </svg>
                  <p style={{ fontSize: "16px", lineHeight: 1.75, color: "#222", margin: 0 }}>A signed and numbered Founding Edition print by artist Bhare, accompanied by a certificate of authenticity</p>
                </div>

              </div>
            </div>

            {/* Closing statement */}
            <p style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: isMobile ? "26px" : "36px", lineHeight: 1.4, color: "#111", maxWidth: "780px", marginBottom: isMobile ? "56px" : "80px" }}>
              The Founding Edition is being created exclusively for MoDDA&rsquo;s Founding Circle and will never exceed 20 prints.
            </p>

            <div style={{ paddingTop: "24px", borderTop: "1px solid #eee", fontSize: "11px", color: "#999", lineHeight: 1.7 }}>
              <p>Tax-deductible contributions to MoDDA are made through Fractured Atlas.</p>
              <p style={{ marginTop: "6px" }}>Cryptocurrency contributions are made directly to MoDDA and are not tax-deductible through Fractured Atlas.</p>
            </div>
          </main>
        } />

        <Route path="/contact" element={
          <main style={{ padding: isMobile ? "56px 24px" : "96px 44px", maxWidth: "720px" }}>
            <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: isMobile ? "48px" : "72px", fontWeight: 400, marginBottom: "28px" }}>Contact</h1>
            <p style={{ color: "#444", lineHeight: 1.75, fontSize: "16px" }}>For inquiries related to the collection, partnerships, press, or artist conversations, send us a message below.</p>
            <ContactForm />
          </main>
        } />
      </Routes>
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "#fff",
            overflow: "auto",
            padding: "44px 24px 80px",
            zIndex: 10,
          }}
        >
          <button
            onClick={() => setSelected(null)}
            style={{
              position: "fixed",
              top: "24px",
              right: "28px",
              background: "#111",
              color: "#fff",
              border: "none",
              borderRadius: "999px",
              padding: "10px 16px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "980px", margin: "0 auto", textAlign: "center", paddingTop: "32px" }}>
            {(selected.animation || selected.mediaType === "video") ? (
              <video src={selected.animation || selected.media} style={{ maxHeight: "78vh", maxWidth: "100%", margin: "0 auto", borderRadius: "6px", backgroundColor: "#f1f1f1" }} autoPlay muted loop playsInline controls />
            ) : selected.media ? (
              <img src={selected.media} alt={`${selected.title} by ${selected.artist}`} style={{ maxHeight: "78vh", maxWidth: "100%", margin: "0 auto", objectFit: "contain", borderRadius: "6px", backgroundColor: "#f1f1f1" }} />
            ) : (
              <div
                style={{
                  aspectRatio: "1 / 1",
                  width: "100%",
                  borderRadius: "6px",
                  backgroundColor: "#f1f1f1",
                  border: "1px solid #e5e5e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#999",
                  fontSize: "13px",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Image Placeholder
              </div>
            )}
            <h2 style={{ fontFamily: "Cormorant Garamond, serif", marginTop: "38px", fontSize: "28px", fontWeight: 500 }}>
              <em>{selected.title}</em>{selected.year ? `, ${selected.year}` : ""}
            </h2>
            <p style={{ fontFamily: "Cormorant Garamond, serif", color: "#555", fontSize: "19px" }}>
              {selected.artist}{selected.country ? `, ${selected.country}` : ""}
            </p>
          </div>
        </div>
      )}
      <NewsletterPopup isMobile={isMobile} />
      <Footer />
    </div>
  );
}
export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
